/**
 * Compresses an image file using HTML5 Canvas while preserving quality.
 * If the file is not an image, or if compression fails/is not beneficial,
 * returns the original file instance safely.
 * 
 * @param {File} file - The original user-uploaded file.
 * @param {number} maxWidth - The target maximum dimension (defaults to 1280px).
 * @param {number} quality - Output quality 0.0 to 1.0 (defaults to 0.7).
 * @returns {Promise<File>} - The compressed image File, or original File.
 */
export const compressImage = (file, maxWidth = 1280, quality = 0.7) => {
  return new Promise((resolve) => {
    // If file is missing, not an image, or extremely small (< 200kb), bypass
    if (!file || !file.type || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate proper aspect ratio scaling
        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxWidth) {
              width = Math.round((width * maxWidth) / height);
              height = maxWidth;
            }
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        
        // Optional white background to prevent transparent PNGs turning black
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, width, height);
        
        // Draw resized image onto canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Export to high-efficiency JPEG blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.warn("[Compressor] Blob extraction failed, falling back to original.");
              resolve(file);
              return;
            }

            // If file output happens to be slightly larger (rare) or equivalent, use original
            if (blob.size >= file.size) {
               resolve(file);
               return;
            }

            // Reconstruct File object from the Blob
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            
            console.log(`[Compressor] Compressed ${file.name}: ${(file.size / 1024).toFixed(1)}KB -> ${(compressedFile.size / 1024).toFixed(1)}KB`);
            resolve(compressedFile);
          },
          "image/jpeg",
          quality
        );
      };

      img.onerror = (err) => {
        console.warn("[Compressor] Image loading failed:", err);
        resolve(file);
      };
    };

    reader.onerror = (err) => {
      console.warn("[Compressor] FileReader failed:", err);
      resolve(file);
    };
  });
};
